/**
 * SOMA Design System v6.0 - GSAP Animation Library
 * Fluid Motion (600-1200ms), Scroll Reveals, Parallax, Orchestrated Animations
 *
 * Version: 6.0.0
 * Last Updated: December 26, 2025
 *
 * Prerequisites:
 * - GSAP 3.12.5+ loaded from CDN
 * - ScrollTrigger plugin loaded
 *
 * Usage in theme.liquid <head>:
 * <script src="https://cdn.jsdelivr.net/npm/gsap@3.12.5/dist/gsap.min.js"></script>
 * <script src="https://cdn.jsdelivr.net/npm/gsap@3.12.5/dist/ScrollTrigger.min.js"></script>
 * <script src="{{ 'soma-v6-animations.js' | asset_url }}" defer></script>
 */

(function() {
  'use strict';

  // Check if GSAP is loaded
  if (typeof gsap === 'undefined') {
    console.warn('SOMA v6.0 Animations: GSAP not loaded. Animations disabled.');
    return;
  }

  // Register ScrollTrigger plugin
  if (typeof ScrollTrigger !== 'undefined') {
    gsap.registerPlugin(ScrollTrigger);
  } else {
    console.warn('SOMA v6.0 Animations: ScrollTrigger not loaded. Scroll animations disabled.');
  }

  // ============================================
  // 1. ACCESSIBILITY: PREFERS REDUCED MOTION
  // ============================================

  const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  if (prefersReducedMotion) {
    console.log('SOMA v6.0 Animations: Reduced motion preference detected. Animations disabled.');
    // Set all elements to visible immediately
    gsap.set('.gsap-animation, .biometric-card, .hero-headline, .hero-subtext, .hero-cta', {
      opacity: 1,
      y: 0,
      x: 0,
      scale: 1
    });
    return; // Exit early - no animations
  }

  // ============================================
  // 2. SCROLL-TRIGGERED CARD REVEALS (600MS FLUID)
  // ============================================

  /**
   * Reveals biometric cards on scroll with fluid 600ms timing
   * Stagger: 150ms between cards
   * Trigger: When card enters 80% of viewport
   */
  function initCardReveals() {
    const cards = document.querySelectorAll('.biometric-card');

    if (cards.length === 0) return;

    gsap.from(cards, {
      scrollTrigger: {
        trigger: cards[0],
        start: "top 80%",
        toggleActions: "play none none none"
      },
      opacity: 0,
      y: 48,
      duration: 0.6, // 600ms fluid
      ease: "cubic-bezier(0.16, 1, 0.3, 1)",
      stagger: 0.15 // 150ms stagger between cards
    });

    console.log(`SOMA v6.0 Animations: Initialized ${cards.length} card reveals`);
  }

  // ============================================
  // 3. HERO ORCHESTRATED REVEAL (1200MS SYMPHONY)
  // ============================================

  /**
   * Orchestrated 5-step hero animation
   * Total duration: ~2.2s luxurious first impression
   * Steps overlap by 300-400ms (not sequential)
   */
  function initHeroOrchestration() {
    const heroBackground = document.querySelector('.hero-background');
    const heroHeadline = document.querySelector('.hero-headline');
    const heroSubtext = document.querySelector('.hero-subtext');
    const heroCTA = document.querySelector('.hero-cta');
    const heroStats = document.querySelectorAll('.hero-stat');

    // Exit if hero elements don't exist
    if (!heroHeadline) return;

    const heroTimeline = gsap.timeline();

    // Step 1: Background fade in (if exists)
    if (heroBackground) {
      heroTimeline.from(heroBackground, {
        opacity: 0,
        duration: 0.8,
        ease: "power2.out"
      });
    }

    // Step 2: Headline slide down (overlap -400ms)
    heroTimeline.from(heroHeadline, {
      opacity: 0,
      y: -32,
      duration: 0.8,
      ease: "cubic-bezier(0.16, 1, 0.3, 1)"
    }, "-=0.4");

    // Step 3: Subtext fade in (overlap -300ms)
    if (heroSubtext) {
      heroTimeline.from(heroSubtext, {
        opacity: 0,
        duration: 0.6,
        ease: "power2.out"
      }, "-=0.3");
    }

    // Step 4: Stats stagger (overlap -200ms)
    if (heroStats.length > 0) {
      heroTimeline.from(heroStats, {
        opacity: 0,
        y: 20,
        duration: 0.6,
        ease: "power2.out",
        stagger: 0.1
      }, "-=0.2");
    }

    // Step 5: CTA bounce in (overlap -200ms)
  
  /**  if (heroCTA) {
      heroTimeline.from(heroCTA, {
        opacity: 0,
        scale: 0.9,
        duration: 0.6,
        ease: "cubic-bezier(0.34, 1.56, 0.64, 1)" // Bounce easing
      }, "-=0.2");
    }
         */

    console.log('SOMA v6.0 Animations: Hero orchestration initialized (~2.2s duration)');
  }

  // ============================================
  // 4. PARALLAX STICKY SECTIONS
  // ============================================

  /**
   * Pins section while user scrolls
   * Background image moves at 0.8x speed (slower than content)
   * Used for lab certificate showcase, product details, etc.
   */
  function initParallaxSections() {
    const stickySections = document.querySelectorAll('.section-sticky');

    if (stickySections.length === 0) return;

    stickySections.forEach(section => {
      const background = section.querySelector('.section-background');

      if (!background) return;

      gsap.to(background, {
        scrollTrigger: {
          trigger: section,
          start: "top top",
          end: "bottom top",
          scrub: 1, // Smooth scrubbing effect
          pin: true
        },
        y: 200, // Background moves slower than content
        ease: "none"
      });
    });

    console.log(`SOMA v6.0 Animations: Initialized ${stickySections.length} parallax sections`);
  }

  // ============================================
  // 5. FOUR PILLARS GRID STAGGER
  // ============================================

  /**
   * Stagger reveal for Four Pillars grid cards
   * 150ms stagger, fluid 600ms timing
   */
  function initFourPillarsReveal() {
    const pillarCards = document.querySelectorAll('.pillar-card');

    if (pillarCards.length === 0) return;

    gsap.from(pillarCards, {
      scrollTrigger: {
        trigger: pillarCards[0],
        start: "top 80%",
        toggleActions: "play none none none"
      },
      opacity: 0,
      y: 48,
      duration: 0.6,
      ease: "cubic-bezier(0.16, 1, 0.3, 1)",
      stagger: 0.15
    });

    console.log(`SOMA v6.0 Animations: Initialized Four Pillars reveal (${pillarCards.length} cards)`);
  }

  // ============================================
  // 6. PRODUCT BADGE PULSE (SUBTLE)
  // ============================================

  /**
   * Subtle pulse glow on product badges
   * 2s duration, infinite loop, low opacity change
   */
  function initBadgePulse() {
    const badges = document.querySelectorAll('.product-badge');

    if (badges.length === 0) return;

    gsap.to(badges, {
      boxShadow: '0 0 20px rgba(170, 255, 132, 0.4)',
      duration: 2,
      ease: "sine.inOut",
      yoyo: true,
      repeat: -1
    });

    console.log(`SOMA v6.0 Animations: Initialized badge pulse (${badges.length} badges)`);
  }

  // ============================================
  // 7. MODAL FADE IN/OUT
  // ============================================

  /**
   * Smooth modal open/close animations
   * Fade overlay + scale content (600ms fluid)
   */
  window.SOMA = window.SOMA || {};

  window.SOMA.openModal = function(modalId) {
    const modal = document.getElementById(modalId);
    if (!modal) return;

    const overlay = modal.querySelector('.modal-overlay');
    const content = modal.querySelector('.modal-content');

    modal.style.display = 'flex';

    const tl = gsap.timeline();
    tl.fromTo(overlay,
      { opacity: 0 },
      { opacity: 1, duration: 0.3, ease: "power2.out" }
    );
    tl.fromTo(content,
      { opacity: 0, scale: 0.9, y: 20 },
      { opacity: 1, scale: 1, y: 0, duration: 0.6, ease: "cubic-bezier(0.16, 1, 0.3, 1)" },
      "-=0.2"
    );
  };

  window.SOMA.closeModal = function(modalId) {
    const modal = document.getElementById(modalId);
    if (!modal) return;

    const overlay = modal.querySelector('.modal-overlay');
    const content = modal.querySelector('.modal-content');

    const tl = gsap.timeline({
      onComplete: () => {
        modal.style.display = 'none';
      }
    });
    tl.to(content,
      { opacity: 0, scale: 0.9, y: 20, duration: 0.3, ease: "power2.in" }
    );
    tl.to(overlay,
      { opacity: 0, duration: 0.2, ease: "power2.in" },
      "-=0.1"
    );
  };

  // ============================================
  // 8. SMOOTH SCROLL TO ANCHOR
  // ============================================

  /**
   * Smooth scroll to anchor links using native scrollIntoView
   * Native browser smooth scrolling (respects reduced motion)
   */
  function initSmoothScroll() {
    const anchorLinks = document.querySelectorAll('a[href^="#"]');

    anchorLinks.forEach(link => {
      link.addEventListener('click', function(e) {
        const href = this.getAttribute('href');

        // Ignore # alone
        if (href === '#') return;

        const target = document.querySelector(href);
        if (!target) return;

        e.preventDefault();

        // Use native scrollIntoView (smooth, accessible, no dependencies)
        target.scrollIntoView({
          behavior: 'smooth',
          block: 'start'
        });

        // Update URL hash
        history.pushState(null, null, href);
      });
    });

    console.log(`SOMA v6.0 Animations: Smooth scroll initialized (${anchorLinks.length} links)`);
  }

  // ============================================
  // 9. LAZY IMAGE REVEAL
  // ============================================

  /**
   * Fade in images as they enter viewport
   * Used for product images, lifestyle photography
   */
  function initLazyImageReveal() {
    const lazyImages = document.querySelectorAll('img[loading="lazy"]');

    if (lazyImages.length === 0) return;

    lazyImages.forEach(img => {
      gsap.from(img, {
        scrollTrigger: {
          trigger: img,
          start: "top 85%",
          toggleActions: "play none none none"
        },
        opacity: 0,
        scale: 1.05,
        duration: 0.8,
        ease: "power2.out"
      });
    });

    console.log(`SOMA v6.0 Animations: Lazy image reveal initialized (${lazyImages.length} images)`);
  }

  // ============================================
  // 10. CUSTOM CURSOR GLOW — REMOVED
  // ============================================
  // Removed: initCursorGlow() was adding inline box-shadow via GSAP
  // to every <button> and <a> on hover, causing green glow rectangles
  // on FAQ items, menu links, and other interactive elements globally.

  // ============================================
  // 11. INITIALIZE ALL ANIMATIONS
  // ============================================

  /**
   * Initialize all animations on DOMContentLoaded
   */
  function init() {
    console.log('SOMA v6.0 Animations: Initializing...');

    // Core animations
    initCardReveals();
    initHeroOrchestration();
    initParallaxSections();
    initFourPillarsReveal();

    // Enhancement animations
    initBadgePulse();
    initSmoothScroll();
    initLazyImageReveal();

    console.log('SOMA v6.0 Animations: All animations initialized ✅');
  }

  // Run on DOMContentLoaded
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

})();

/**
 * ============================================
 * USAGE EXAMPLES
 * ============================================
 *
 * 1. Hero Section:
 * <section class="hero">
 *   <div class="hero-background"></div>
 *   <h1 class="hero-headline">Your Headline</h1>
 *   <p class="hero-subtext">Your subtext</p>
 *   <button class="hero-cta button-primary">CTA</button>
 * </section>
 *
 * 2. Biometric Cards:
 * <div class="biometric-card">
 *   <div class="card-metric">
 *     <span class="metric-value">23%</span>
 *     <span class="metric-label">Wzrost NGF</span>
 *   </div>
 *   <p class="card-description">Description</p>
 * </div>
 *
 * 3. Parallax Section:
 * <section class="section-sticky">
 *   <div class="section-background">
 *     <img src="background.jpg" alt="">
 *   </div>
 *   <div class="section-content">
 *     <!-- Content here -->
 *   </div>
 * </section>
 *
 * 4. Modal:
 * <button onclick="SOMA.openModal('myModal')">Open Modal</button>
 * <div id="myModal" class="modal" style="display: none;">
 *   <div class="modal-overlay" onclick="SOMA.closeModal('myModal')">
 *     <div class="modal-content">
 *       <button class="modal-close" onclick="SOMA.closeModal('myModal')">×</button>
 *       <!-- Modal content -->
 *     </div>
 *   </div>
 * </div>
 *
 * ============================================
 * END OF SOMA V6.0 ANIMATIONS
 * ============================================
 */
